"use client"

import { useCallback, useState } from "react"
import { toast } from "sonner"

interface DownloadOptions {
  filename: string
  mimeType?: string
}

export function useFileDownload() {
  const [downloading, setDownloading] = useState(false)

  const downloadBlob = useCallback(
    async (blob: Blob, options: DownloadOptions) => {
      setDownloading(true)
      try {
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = options.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        toast.success(`Downloaded ${options.filename}`)
        return true
      } catch {
        toast.error("Download failed")
        return false
      } finally {
        setDownloading(false)
      }
    },
    []
  )

  const downloadText = useCallback(
    async (text: string, options: DownloadOptions) => {
      const blob = new Blob([text], { type: options.mimeType || "text/plain" })
      return downloadBlob(blob, options)
    },
    [downloadBlob]
  )

  const downloadJson = useCallback(
    async (data: unknown, filename: string) => {
      const text = JSON.stringify(data, null, 2)
      return downloadText(text, { filename, mimeType: "application/json" })
    },
    [downloadText]
  )

  return { downloadBlob, downloadText, downloadJson, downloading }
}
